import React, { useState, useEffect, useMemo } from 'react';
import {
	FormControl,
	MenuItem,
	Select,
	useMediaQuery,
	ListItemIcon,
	Box,
	ListSubheader,
	Divider,
} from '@mui/material';
import PropTypes from 'prop-types';
import moment from 'moment';
import { getDateRange } from './util';
import { useStyles, selectStyleMenu } from './styles';
import { isSaturday } from '../../helper/date';
import ShowCalendar from '../Calendar/ShowCalender';
import { DateRangeContext } from '../../context/appcontext';
import { BOGLE_REGULAR, walmartWeekMap } from '../../constants/constant';
import { colors } from '../../theme/color-palette';
import CALENDARSVG from '../../assets/SVGs/Calendar.svg';
import CheckIcon from '@mui/icons-material/Check';
import {
	getPastWMTWeekDateRange,
	getWMTWeekToDate,
	getWeekNo,
} from '../../helper/date/walmart-week';
import analytics from '../../helper/analytics';
import tracker from '../../constants/tracker';
import { useLocation } from 'react-router-dom';
export default function WalmartWeekFilter({
	weekFilter,
	handleWeekChange,
	setWeekFilter,
	startWeek,
	endWeek,
	startDate,
	endDate,
	isStoreDrilldown,
}) {
	const [anchorEl, setAnchorEl] = useState(null);
	const [open, setOpen] = useState(false);
	const [showTickOnCustom, setshowTickOnCustom] = useState(false);
	const [dateRange, setDateRange] = useState({
		start: null,
		end: null,
		week1: null,
		week2: null,
	});
	const [calendarUpdate, setCalendarUpdate] = useState(false);
	const [showWk, setShowWk] = useState(true);
	const [showWalCal, setShowWalCal] = useState(true);
	const matches = useMediaQuery('(min-width:360px)');
	const classes = useStyles();
	const { PURPLE, LIGHT_GRAY_HEX } = colors;
	const location = useLocation();
	const handleCustomWeek = (e) => {
		setshowTickOnCustom(true);
		e.stopPropagation();
		setAnchorEl(e.target.parentNode);
		analytics.trackEventInfo({
			type: tracker.EVENTS.CLICK,
			actionOn: tracker.ACTION_ON.CUSTOMWEEK_OPENED,
		});
	};

	const closeCalendar = () => {
		setshowTickOnCustom(false);
		setAnchorEl(null);
	};

	const handleSetDateRange = (selection) => {
		const { start, end, week1, week2 } = selection;
		setDateRange({ start, end, week1, week2 });
	};

	const setDefaultDateRange = () => {
		if (startDate || endDate) {
			setDateRange({
				start: moment(startDate),
				end: moment(endDate),
				week1: startWeek,
				week2: endWeek,
			});
		} else {
			if (weekFilter > 0) {
				setDateRange({
					start: moment(
						getPastWMTWeekDateRange(weekFilter, 'MM-DD-YYYY')?.startDate
					),
					end: moment(
						getPastWMTWeekDateRange(weekFilter, 'MM-DD-YYYY')?.endDate
					),
					week1: getWeekNo(
						moment(getPastWMTWeekDateRange(weekFilter, 'MM-DD-YYYY')?.startDate)
					),
					week2: getWeekNo(
						moment(getPastWMTWeekDateRange(weekFilter, 'MM-DD-YYYY')?.endDate)
					),
				});
			} else if (weekFilter === 0) {
				setDateRange({
					start: moment(getWMTWeekToDate('MM-DD-YYYY')?.startDate),
					end: moment(getWMTWeekToDate('MM-DD-YYYY')?.endDate),
					week1: getWeekNo(moment(getWMTWeekToDate('MM-DD-YYYY')?.startDate)),
					week2: getWeekNo(moment(getWMTWeekToDate('MM-DD-YYYY')?.endDate)),
				});
			}
		}
	};

	useEffect(() => {
		setDefaultDateRange();
	}, [weekFilter]);

	const handleClose = () => {
		setshowTickOnCustom(false);
		setOpen(false);
		//setDefaultDateRange(); // Enabling this will affect the custom date
	};

	const handleOpen = () => {
		setOpen(true);
	};

	const dateRangeValue = useMemo(
		() => ({
			handleSetDateRange: handleSetDateRange,
			setDateRange: setDateRange,
			dateRange: dateRange,
			showWk: true,
			setShowWk: setShowWk,
			showWalCal: true,
			setShowWalCal: setShowWalCal,
			singleWkSelEnable: false,
			calendarUpdate: calendarUpdate,
			setCalendarUpdate: setCalendarUpdate,
		}),
		[]
	);

	return (
		<FormControl
			className={classes.formControl}
			size="small"
			sx={{ minWidth: matches ? 170 : 20, width: '100%' }}>
			<Select
				data-test="walmart-week-filter"
				id="simple-select-Week"
				value={weekFilter}
				renderValue={(value) => {
					return (
						<Box
							sx={{
								display: 'flex',
								gap: 1,
								alignItems: 'center',
							}}>
							{!isStoreDrilldown && (
								<img src={CALENDARSVG} alt="Channel partner"></img>
							)}
							{`${getDateRange(
								value,
								walmartWeekMap[value],
								startWeek,
								endWeek,
								dateRange.start,
								dateRange.end
							)}`}
						</Box>
					);
				}}
				onChange={handleWeekChange}
				open={open}
				onClose={handleClose}
				onOpen={handleOpen}
				className={open ? classes.onFocus : ''}
				sx={{
					fontFamily: BOGLE_REGULAR,
					fontSize: '14px !important',
					height: '32px',
					'& .MuiSelect-select': {
						fontFamily: BOGLE_REGULAR,
						display: 'flex',
						alignItems: 'center',
					},
					'& .MuiSvgIcon-root': {
						color: PURPLE,
					},
				}}
				MenuProps={{
					PaperProps: {
						sx: {
							height: isStoreDrilldown ? '268px' : '420px',
						},
					},
					sx: selectStyleMenu,
					disablePortal: true,
				}}>
				{!isStoreDrilldown && (
					<ListSubheader
						sx={{
							fontSize: '12px',
							fontFamily: BOGLE_REGULAR,
							fontWeight: 500,
							lineHeight: '16px',
							padding: '22px 16px',
						}}>
						Walmart Week
					</ListSubheader>
				)}
				{Object.entries(walmartWeekMap)?.map(([key, value]) => (
					<MenuItem
						disabled={
							(key === '0' && isSaturday) ||
							(location?.pathname?.includes('/dashboard/guided-insights') &&
								['0', '1'].includes(key)) ||
							(location?.pathname?.includes('/details/store-drilldown') &&
								['0', '1'].includes(key))
						}
						key={key}
						value={key}
						data-test={`week-filter-${key}`}
						onClickCapture={key === '-1' ? (e) => handleCustomWeek(e) : null}
						style={{
							display: 'flex',
							justifyContent: 'flex-start',
							paddingBottom: '8px',
							paddingTop: '6px',
						}}
						sx={{
							fontSize: 14,
							fontFamily: BOGLE_REGULAR,
							lineHeight: '20px',
							fontWeight: 500,
							padding: isStoreDrilldown ? '8px 8px' : '10px 16px',
						}}>
						<ListItemIcon>
							{(weekFilter == key && weekFilter != -1 && !showTickOnCustom) ||
							(weekFilter === -1 && key == -1) ||
							(showTickOnCustom && key == -1) ? (
								<CheckIcon
									style={
										isStoreDrilldown
											? {
													height: '16px',
													width: '16px',
													color: LIGHT_GRAY_HEX,
											  }
											: {}
									}
								/>
							) : null}
						</ListItemIcon>
						{`${getDateRange(
							key,
							value,
							startWeek,
							endWeek,
							dateRange.start,
							dateRange.end
						)}`}
					</MenuItem>
				))}
				{!isStoreDrilldown && <Divider className={classes.border} />}
			</Select>
			{Boolean(anchorEl) && (
				<DateRangeContext.Provider
					value={{
						dateRange,
						setDateRange,
						dateRangeValue,
						calendarUpdate,
						setCalendarUpdate,
						showWk,
						setShowWk,
						showWalCal,
						setShowWalCal,
						handleSetDateRange,
					}}>
					<ShowCalendar
						open={Boolean(anchorEl)}
						anchorEl={anchorEl}
						cbHandleClose={closeCalendar}
						setWeekFilter={setWeekFilter}
						handleCloseMenu={handleClose}
						handleWeekChange={handleWeekChange}
					/>
				</DateRangeContext.Provider>
			)}
		</FormControl>
	);
}

WalmartWeekFilter.propTypes = {
	weekFilter: PropTypes.number,
	handleWeekChange: PropTypes.func,
	setWeekFilter: PropTypes.func,
	startWeek: PropTypes.number,
	endWeek: PropTypes.number,
	startDate: PropTypes.string,
	endDate: PropTypes.string,
	isStoreDrilldown: PropTypes.bool,
};
